import React from 'react';

import { ACCOUNT_PATHS } from '@/lib/accountPaths';
import {
  SETTINGS_PAGE_TITLES,
  settingsCrmIsNative,
} from '@/lib/settingsPaths';
import { useAccountRecord } from '@/pages/account/AccountRecordContext';
import { accountCount } from '@/pages/account/accountModel';
import {
  AccountCard,
  CardColumn,
  InlineSelect,
  NavLink,
  NotYetButton,
  Row,
  ScopeChip,
  SmallButton,
  StatusChip,
} from '@/pages/account/primitives';
import { SettingsPageShell } from './SettingsPageShell';
import { SETTINGS_CARDS } from './settingsSearchIndex';

/**
 * AR-390 — CRM Settings.
 *
 * D-ACCT-38 (Jason 2026-09-18): in Phase 1 the CRM runs on WorldVia PRO, so
 * this page is ONE card with the way in — what the retired Products page used
 * to carry for it. The native page arrives in Phase 2, and the builder for it
 * is here already so the two cannot describe the same settings differently.
 *
 * BACK-END REQUIREMENT: a single sign-on deep link into the PRO CRM settings
 * page. No address is on file for it, so the button is inert and says what it
 * is waiting on — the treatment every Phase-1 SSO bridge takes.
 */

export const SETTINGS_PRO_WHY =
  'Single sign-on into your WorldVia.pro settings arrives with the build — Member Success opens the page with you meanwhile';

const TASK_DAYS = ['1', '2', '3', '5', '7'];
const STALL_DAYS = ['7', '14', '21', '30'];

export const SettingsCrm: React.FC = () => {
  const { record, setLive } = useAccountRecord();
  const c = record.crm;
  const native = settingsCrmIsNative();

  if (!native) {
    return (
      <SettingsPageShell page="crm">
        <CardColumn>
          <AccountCard name={SETTINGS_CARDS.crmWayIn.name}>
            <Row label="CRM" value={SETTINGS_PAGE_TITLES.crm[1]} last />
            <div className="pt-2">
              <NotYetButton note={SETTINGS_PRO_WHY}>
                Open CRM settings in WorldVia PRO ↗
              </NotYetButton>
            </div>
          </AccountCard>
        </CardColumn>
      </SettingsPageShell>
    );
  }

  return (
    <SettingsPageShell page="crm">
      <CardColumn>
        <AccountCard name={SETTINGS_CARDS.crmStatus.name}>
          <Row label="CRM version" value="Current" chip={<StatusChip status="Connected" />} />
          <Row label="Open" value={<NavLink to="/crm">open CRM →</NavLink>} />
          <Row
            label="Email connection"
            value={c.gmail ? 'Gmail' : ''}
            chip={<StatusChip status={c.gmail ? 'Connected' : 'Not connected'} />}
            last
          />
          <div className="pt-2">
            <SmallButton onClick={() => setLive('crm.gmail', !c.gmail)}>
              {c.gmail ? 'Disconnect Gmail' : 'Connect Gmail'}
            </SmallButton>
          </div>
        </AccountCard>

        <AccountCard
          cardKey="crm"
          name={SETTINGS_CARDS.crmFlow.name}
          chip={<ScopeChip scope="personal" />}
          form={
            <>
              <p className="text-[15px] text-gray-900 dark:text-gray-100">
                Remind me <InlineSelect path="crm.taskDays" options={TASK_DAYS} label="Task reminder days" /> days
                before a task is due
              </p>
              <p className="text-[15px] text-gray-900 dark:text-gray-100">
                Check in <InlineSelect path="crm.stallDays" options={STALL_DAYS} label="Stalled opportunity days" /> days
                after an opportunity stalls
              </p>
            </>
          }
        >
          <Row
            label="Task reminders"
            value={`Remind me ${accountCount(c.taskDays, 'day', 'days')} before a task is due`}
          />
          <Row
            label="Stalled opportunities"
            value={`Check in ${accountCount(c.stallDays, 'day', 'days')} after an opportunity stalls`}
            last
          />
        </AccountCard>

        <AccountCard name={SETTINGS_CARDS.crmData.name}>
          <Row label="Last CSV import" value={c.importedOn} last />
          <div className="pt-2">
            <NotYetButton note="Contact import lands with the CRM build — Member Success runs one for you today">
              Import data
            </NotYetButton>
          </div>
        </AccountCard>

        <AccountCard name={SETTINGS_CARDS.crmTeam.name} chip={<ScopeChip scope="owner" />}>
          <Row
            label="CRM access"
            value={record.teamset.crm}
            extra={<NavLink to={ACCOUNT_PATHS.team}>Managed under Team + Access →</NavLink>}
            last
          />
        </AccountCard>

        <AccountCard name={SETTINGS_CARDS.crmStyles.name}>
          <Row
            label="Travel styles"
            value="Uses your public profile's list"
            extra={<NavLink to={ACCOUNT_PATHS.public}>Brand + Public Presence →</NavLink>}
            last
          />
        </AccountCard>
      </CardColumn>
    </SettingsPageShell>
  );
};

export default SettingsCrm;
